import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { buildInfo } from '../../environments/build-info';
import { ApiService } from '../_services/api.service';
import { MdrDocsResponse, MdrSection, MdrAddress } from '../_interfaces/mdr-doc';
import { languageNameFromCode } from '../_helpers/utils';
import { formatDmyDate } from '../_helpers/dmy-date';
import { AnalyticsService } from '../_services/analytics.service';

const BOLD_TAG_PATTERN = /^<b>(.*)<\/b>$/i;
const NUMBERING_PATTERN = /^\d+(\.\d+)*\.?\s+/;

const BLOCK_ICONS: Record<string, string> = {
  intended: 'person_outline',
  warning: 'warning_amber',
};

interface MdrBlock {
  title: string;
  icon: string;
  sections: AccordionSection[];
  expanded: boolean;
}

interface AccordionSection {
  title: string;
  content: string;
  bold: boolean;
  expanded: boolean;
}

@Component({
  selector: 'app-mdr-information',
  imports: [
    CommonModule,
    TranslateModule,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './mdr-information.component.html',
  styleUrl: './mdr-information.component.scss',
  host: {
    class: 'block h-full p-4 md:px-10 md:pt-8 md:pb-14 overflow-y-auto'
  }
})
export class MdrInformationComponent implements OnInit {
  readonly deviceName = 'T-Pro Scribe';
  readonly appVersion = environment.version;

  manufacturerAddress: MdrAddress | null = null;
  providerName = '';
  blocks: MdrBlock[] = [];
  releaseDate = '';
  loading = true;

  private readonly analytics = inject(AnalyticsService);

  constructor(
    private apiService: ApiService,
    private translate: TranslateService,
  ) {}

  async ngOnInit(): Promise<void> {
    const language = languageNameFromCode(this.translate.currentLang || 'en');
    try {
      const response = await this.apiService.getMdrDocs(language);
      this.blocks = this.groupIntoBlocks(response.sections);
      this.releaseDate = this.formatDate(buildInfo.buildDate);
      this.manufacturerAddress = response.manufacturerAddresses?.[0] ?? null;
      this.providerName = response.providers?.[0]?.title ?? '';
    } catch {
      this.blocks = [];
    }
    this.loading = false;
    this.analytics.track('mdr_page_viewed');
  }

  toggleBlock(index: number): void {
    const block = this.blocks[index];
    block.expanded = !block.expanded;
    for (const section of block.sections) {
      section.expanded = block.expanded;
    }
  }

  syncBlockExpanded(blockIdx: number): void {
    const block = this.blocks[blockIdx];
    block.expanded = block.sections.length > 0 && block.sections.every(s => s.expanded);
  }

  private groupIntoBlocks(sections: MdrSection[]): MdrBlock[] {
    const blocks: MdrBlock[] = [];

    for (const section of sections) {
      const parsed = this.parseSection(section);

      if (parsed.bold) {
        blocks.push({
          title: parsed.title,
          icon: this.resolveIcon(parsed.title),
          sections: [parsed],
          expanded: false,
        });
        continue;
      }

      if (blocks.length === 0) {
        blocks.push({ title: parsed.title, icon: 'info', sections: [], expanded: false });
      }
      blocks[blocks.length - 1].sections.push(parsed);
    }

    return blocks;
  }

  private parseSection(section: MdrSection): AccordionSection {
    const trimmedTitle = section.title.trim();
    const boldMatch = BOLD_TAG_PATTERN.exec(trimmedTitle);
    const isBold = boldMatch !== null;
    const cleanTitle = isBold ? boldMatch[1].trim() : trimmedTitle;

    return {
      title: cleanTitle.replace(NUMBERING_PATTERN, ''),
      content: section.content.replaceAll('{{deviceName}}', this.deviceName),
      bold: isBold,
      expanded: false,
    };
  }

  private resolveIcon(title: string): string {
    const lower = title.toLowerCase();
    for (const [keyword, icon] of Object.entries(BLOCK_ICONS)) {
      if (lower.includes(keyword)) return icon;
    }
    return 'info';
  }

  private formatDate(isoDate: string): string {
    if (isoDate === 'local-dev') {
      return 'local-dev';
    }
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
      return isoDate;
    }
    return formatDmyDate(date);
  }
}
